import type {
  ToolErrorCode,
  ToolOperation,
  ToolSuccessDimensions,
} from '@saas-pages/shared/public/runtime'
import { startScannerOperation } from '@/pages/qr-scanner/scannerAnalytics'
import { createScannerDecoder, type ScannerDecoder } from '@/pages/qr-scanner/scannerDecoder'
import type { ScanInputMethod } from '@/pages/qr-scanner/scannerModel'

export interface ScannerOperationToken {
  generation: number
  inputMethod: ScanInputMethod
  startedAt: number
  operation: ToolOperation
  decoder: ScannerDecoder
}

export class ScannerOperationOwner {
  private generation = 0
  private current: ScannerOperationToken | null = null

  constructor(
    private readonly decoderFactory: () => ScannerDecoder = createScannerDecoder,
    private readonly operationFactory: (method: ScanInputMethod) => ToolOperation = startScannerOperation,
  ) {}

  begin(inputMethod: ScanInputMethod, startedAt: number): ScannerOperationToken {
    this.cancel('cancelled')
    this.generation += 1
    const decoder = this.decoderFactory()
    try {
      const token = {
        generation: this.generation,
        inputMethod,
        startedAt,
        operation: this.operationFactory(inputMethod),
        decoder,
      }
      this.current = token
      return token
    } catch (cause) {
      decoder.terminate()
      this.generation += 1
      throw cause
    }
  }

  isCurrent(token: ScannerOperationToken): boolean {
    return this.current === token && token.generation === this.generation
  }

  success(
    token: ScannerOperationToken,
    durationMs: number,
    dimensions?: ToolSuccessDimensions,
  ): boolean {
    if (!this.isCurrent(token)) return false
    token.operation.success(durationMs, dimensions)
    this.release(token)
    return true
  }

  error(token: ScannerOperationToken, errorCode: ToolErrorCode): boolean {
    if (!this.isCurrent(token)) return false
    token.operation.error(errorCode)
    this.release(token)
    return true
  }

  cancel(errorCode: ToolErrorCode = 'cancelled'): void {
    const token = this.current
    if (!token) return
    token.operation.error(errorCode)
    this.release(token)
  }

  private release(token: ScannerOperationToken): void {
    token.decoder.terminate()
    if (this.current === token) this.current = null
    this.generation += 1
  }
}
